// ExportReportModal.tsx — Sprint 14. The Overview tab's "Export
// Report" dropdown: report type / date range / format / options.
import React from 'react';
import {
  Alert, Button, Col, DatePicker, Form, FormInstance, Input, Modal, Radio, Row, Select,
  Space, Switch,
} from 'antd';
import {
  CalendarOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined, FileWordOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

export interface ExportReportModalProps {
  open:    boolean;
  form:    FormInstance;
  loading: boolean;
  onCancel: () => void;
  onExport: () => void;
}

export const ExportReportModal: React.FC<ExportReportModalProps> = ({
  open, form, loading, onCancel, onExport,
}) => (
  <Modal
    title={<Space><FilePdfOutlined style={{ color: '#ed8936' }} /><span>Export Report</span></Space>}
    open={open}
    onCancel={onCancel}
    footer={[
      <Button key="cancel" onClick={onCancel}>Cancel</Button>,
      <Button key="export" type="primary" loading={loading} onClick={onExport} icon={<DownloadOutlined />}>
        Export Report
      </Button>,
    ]}
    width={600}
    centered
  >
    <Form
      form={form} layout="vertical"
      initialValues={{ format: 'pdf', range: 'month', includeCharts: true, includeDetails: true }}
    >
      <Form.Item
        name="reportType" label="Report Type"
        rules={[{ required: true, message: 'Please select report type' }]}
      >
        <Select placeholder="Select report type">
          <Option value="financial">Financial Summary</Option>
          <Option value="collection">Collection Report</Option>
          <Option value="occupancy">Occupancy Report</Option>
          <Option value="maintenance">Maintenance Report</Option>
          <Option value="full">Full Dashboard Report</Option>
        </Select>
      </Form.Item>

      <Form.Item name="dateRange" label="Date Range">
        <RangePicker style={{ width: '100%' }} suffixIcon={<CalendarOutlined />} />
      </Form.Item>

      <Form.Item
        name="format" label="Export Format"
        rules={[{ required: true, message: 'Please select format' }]}
      >
        <Radio.Group style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Radio.Button value="pdf"   style={{ width: '100%', textAlign: 'center' }}>
                <FilePdfOutlined /> PDF
              </Radio.Button>
            </Col>
            <Col span={8}>
              <Radio.Button value="excel" style={{ width: '100%', textAlign: 'center' }}>
                <FileExcelOutlined /> Excel
              </Radio.Button>
            </Col>
            <Col span={8}>
              <Radio.Button value="word"  style={{ width: '100%', textAlign: 'center' }}>
                <FileWordOutlined /> Word
              </Radio.Button>
            </Col>
          </Row>
        </Radio.Group>
      </Form.Item>

      <Form.Item label="Report Options">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="includeCharts" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            {' '}Include Charts
          </Col>
          <Col span={12}>
            <Form.Item name="includeDetails" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            {' '}Detailed Breakdown
          </Col>
        </Row>
      </Form.Item>

      <Form.Item name="notes" label="Additional Notes">
        <TextArea rows={2} placeholder="Add any notes for the report..." maxLength={500} showCount />
      </Form.Item>

      <Alert
        message="Export Information"
        description="The report will include all selected data and can take a few moments to generate."
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </Form>
  </Modal>
);
